import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PageSEO from '@/components/feature/PageSEO';
import { useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const article = useMemo(() => articles.find((a) => a.slug === slug), [slug]);
  const category = useMemo(
    () => (article ? categories.find((c) => c.slug === article.category) : null),
    [article]
  );

  const relatedArticles = useMemo(
    () =>
      article
        ? articles
            .filter((a) => a.category === article.category && a.slug !== article.slug)
            .slice(0, 3)
        : [],
    [article]
  );

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen bg-background-50 pt-16 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-400 mb-4">
              <i className="ri-file-unknow-line text-2xl"></i>
            </div>
            <h2 className="text-xl font-heading font-bold text-foreground-800 mb-2">文章未找到</h2>
            <p className="text-sm text-foreground-500 mb-6">您访问的文章不存在或已被移除</p>
            <Link
              to="/"
              className="px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const contentParagraphs = article.content.split('\n\n').filter(Boolean);

  return (
    <>
      <PageSEO />
      <Navbar />
      <main className="min-h-screen bg-background-50 pt-16">
        <article className="max-w-4xl mx-auto px-4 md:px-6 py-10 md:py-14">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            {category && (
              <>
                <Link to={`/category/${category.slug}`} className="hover:text-primary-600 transition-colors cursor-pointer">
                  {category.name}
                </Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
              </>
            )}
            <span className="text-foreground-800 font-medium truncate max-w-[200px]">{article.title}</span>
          </nav>

          <div className="mb-8">
            <div className="flex items-center gap-2 flex-wrap mb-3">
              {article.tags.map((tag) => (
                <span key={tag} className="px-2.5 py-0.5 rounded-full bg-accent-100 text-accent-700 text-xs">
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 leading-tight mb-4">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-sm text-foreground-500">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full overflow-hidden bg-background-200">
                  <img src={article.authorAvatar} alt={article.author} className="w-full h-full object-cover" />
                </div>
                <span className="text-foreground-700 font-medium">{article.author}</span>
              </div>
              <span>·</span>
              <span>{article.publishDate}</span>
              <span>·</span>
              <span>{article.readTime}</span>
            </div>
          </div>

          <div className="relative h-64 md:h-80 lg:h-96 rounded-xl overflow-hidden mb-10">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </div>

          <div className="prose-custom max-w-none">
            {contentParagraphs.map((paragraph, index) => {
              if (paragraph.startsWith('## ')) {
                return (
                  <h2 key={index} className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10 mb-4">
                    {paragraph.replace('## ', '')}
                  </h2>
                );
              }
              if (paragraph.startsWith('### ')) {
                return (
                  <h3 key={index} className="text-lg md:text-xl font-heading font-semibold text-foreground-800 mt-8 mb-3">
                    {paragraph.replace('### ', '')}
                  </h3>
                );
              }
              if (paragraph.startsWith('- ')) {
                const items = paragraph.split('\n').map((line) => line.replace('- ', '').trim());
                return (
                  <ul key={index} className="list-disc list-inside space-y-1.5 mb-5 text-foreground-700 leading-relaxed">
                    {items.map((item, i) => (
                      <li key={i} className="text-sm md:text-base pl-1">{item}</li>
                    ))}
                  </ul>
                );
              }
              return (
                <p key={index} className="text-sm md:text-base text-foreground-700 leading-loose mb-5">
                  {paragraph.split('\n').map((line, lineIndex) => (
                    <span key={lineIndex}>
                      {lineIndex > 0 && <br />}
                      {line}
                    </span>
                  ))}
                </p>
              );
            })}
          </div>

          <div className="mt-12 pt-8 border-t border-background-200/70">
            <div className="flex items-center gap-4 p-5 rounded-xl bg-background-100 border border-background-200/70">
              <div className="w-14 h-14 rounded-full overflow-hidden bg-background-200 flex-shrink-0">
                <img src={article.authorAvatar} alt={article.author} className="w-full h-full object-cover" />
              </div>
              <div>
                <div className="text-sm font-semibold text-foreground-900">{article.author}</div>
                <div className="text-xs text-foreground-500 mt-0.5">91传媒特约作者 · 专注数字媒体领域深度研究</div>
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="max-w-4xl mx-auto px-4 md:px-6 pb-16 md:pb-24">
            <h3 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8">
              相关推荐
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {relatedArticles.map((ra) => (
                <Link
                  key={ra.slug}
                  to={`/article/${ra.slug}`}
                  className="group rounded-xl overflow-hidden bg-background-100 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="relative h-40 overflow-hidden">
                    <img
                      src={ra.coverImage}
                      alt={ra.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <h4 className="text-sm font-heading font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {ra.title}
                    </h4>
                    <div className="text-xs text-foreground-500 mt-2">
                      {ra.publishDate} · {ra.readTime}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}